import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost } from '../../../services/apiClient';
import { completePurchase } from '../../../services/ateliersPacksService';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import Divider from '@/components/ui/Divider';
import Typography from '@/components/ui/Typography';
import PackCard from '@/components/ui/PackCard';
import { useAuth } from '@/contexts/AuthContext';

/**
 * AchatPacksAteliers - Purchase page for ateliers packs
 *
 * - Fetches available packs and user's purchased packs
 * - Shows Available / Purchased lists
 * - Simple cart: add/remove packs and pay
 * - On successful payment, calls pack purchase endpoint to register purchases
 */
export default function AchatPacksAteliers() {
  const navigate = useNavigate();

  const { user } = useAuth();

  const [packs, setPacks] = useState([]);
  const [packsLoading, setPacksLoading] = useState(false);
  const [packsError, setPacksError] = useState('');

  const [userPacks, setUserPacks] = useState([]); // array of pack IDs
  const [userPacksLoading, setUserPacksLoading] = useState(false);
  const [userPacksError, setUserPacksError] = useState('');

  const [cart, setCart] = useState(new Set());
  const [isPaying, setIsPaying] = useState(false);
  const [message, setMessage] = useState('');

  const fetchPacks = useCallback(async () => {
    setPacksLoading(true);
    setPacksError('');
    try {
      const res = await apiGet('/ateliersPacks');
      const list = res?.packs ?? [];
      setPacks(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Failed to load packs', err);
      setPacksError('Impossible de charger les packs.');
      setPacks([]);
    } finally {
      setPacksLoading(false);
    }
  }, []);

  const fetchUserPacks = useCallback(async () => {
    if (!user?.uid) return;

    setUserPacksLoading(true);
    setUserPacksError('');

    try {
      const res = await apiGet('/users/' + user.uid + '/ateliersPacks');
      const list = res?.packs ?? [];
      setUserPacks(Array.isArray(list) ? list.map((p) => p.id ?? p) : []);
    } catch (err) {
      console.warn('Failed to fetch user packs (me)', err);
      setUserPacks([]);
    } finally {
      setUserPacksLoading(false);
    }
  }, [user.uid]);

  useEffect(() => {
    fetchPacks();
    fetchUserPacks();
  }, [fetchPacks, fetchUserPacks]);

  const availablePacks = useMemo(
    () => packs.filter((p) => !userPacks.includes(p.id)),
    [packs, userPacks]
  );
  const purchasedPacks = useMemo(
    () => packs.filter((p) => userPacks.includes(p.id)),
    [packs, userPacks]
  );

  const toggleCart = (packId) => {
    setCart((prev) => {
      const next = new Set(prev);
      if (next.has(packId)) next.delete(packId);
      else next.add(packId);
      return next;
    });
  };

  const cartItems = useMemo(() => packs.filter((p) => cart.has(p.id)), [packs, cart]);
  const cartTotal = useMemo(
    () => cartItems.reduce((sum, p) => sum + (typeof p.price === 'number' ? p.price : 0), 0),
    [cartItems]
  );

  // Replace handlePay with navigation to payment page
  const handlePay = async () => {
    if (!cartItems.length) return;

    const packsIds = cartItems.map((p) => p.id);

    const response = await apiPost('/stripe/create-ateliers-packs-checkout-session', {
      packsIds,
      cancelUrl: window.location.href,
      // cancelUrl: `${window.location.origin}/ateliers/payment-success`,
      successUrl: `${window.location.origin}/ateliers/payment-success`
    });

    const { sessionUrl } = response;

    window.location.href = sessionUrl;
  };

  // Add this useEffect to handle the redirect back from Stripe
  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const sessionId = query.get('session_id');

    const handleCheckoutSuccess = async () => {
      if (sessionId) {
        try {
          // 3) Validate payment and add packs to user
          await completePurchase({
            packIds: cartItems.map((p) => p.id),
            paymentIntentId: sessionId
          });

          // Refresh user packs & clear cart
          await fetchUserPacks();
          setCart(new Set());
          setMessage('Paiement réussi. Les packs ont été ajoutés à votre compte.');

          // Clean up the URL
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch (err) {
          console.error('Purchase completion error', err);
          setMessage(err.message || "Échec de la finalisation de l'achat.");
        }
      }
    };

    handleCheckoutSuccess();
  }, [cartItems, fetchUserPacks]);

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold text-neutral-900">{`Acheter des packs d'ateliers`}</h2>
        <div>
          <Button variant="secondary" onClick={() => navigate(-1)}>
            Retour
          </Button>
        </div>
      </div>

      {packsLoading || userPacksLoading ? (
        <Typography variant="body">Chargement…</Typography>
      ) : packsError ? (
        <Typography variant="body" style={{ color: '#e11d48' }}>
          {packsError}
        </Typography>
      ) : (
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-4 gap-6 flex flex-col">
            {/* Packs disponibles */}
            {availablePacks.length > 0 && (
              <div>
                <Typography variant="h3">Packs disponibles</Typography>
                <div className="space-y-3 mt-3">
                  {availablePacks.map((p) => (
                    <PackCard
                      key={p.id}
                      pack={p}
                      isOwned={false}
                      inCart={cart.has(p.id)}
                      onToggleCart={toggleCart}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Mes packs */}
            {purchasedPacks.length > 0 && (
              <div>
                <Typography variant="h3" className="mt-6">
                  Mes packs
                </Typography>
                <div className="space-y-3 mt-3">
                  {purchasedPacks.map((p) => (
                    <PackCard key={p.id} pack={p} isOwned />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Cart Summary */}
          <aside className="h-fit space-y-4 mt-[46px]">
            <Card className="bg-neutral-50 shadow-lg border border-neutral-200 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <Typography variant="h2" bold>
                  Panier
                </Typography>
                <Typography variant="body" className="text-neutral-600">
                  {cartItems.length} élément(s)
                </Typography>
              </div>

              <Divider className="my-3" />

              <div
                className="max-h-[320px] overflow-y-auto transition-all duration-300"
                aria-live="polite"
              >
                {cartItems.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-neutral-500">
                    {/* Use icon component if available, else emoji */}
                    <p className="w-10 h-10 mb-2" aria-hidden="true">
                      🛒
                    </p>
                    <Typography variant="body" bold>
                      Votre panier est vide
                    </Typography>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {cartItems.map((p, idx) => (
                      <div
                        key={p.id}
                        className="flex items-center justify-between px-2 py-2 bg-white rounded-md shadow-sm transition-all duration-200 hover:bg-neutral-100"
                      >
                        <div>
                          <Typography variant="body1" bold>
                            {p.name}
                          </Typography>
                          <Typography variant="caption" className="text-neutral-600">
                            {p.description}
                          </Typography>
                        </div>
                        <div className="flex flex-col items-center gap-3">
                          <Typography variant="body2" bold>
                            €{(p.price ?? 0).toFixed(2)}
                          </Typography>
                          <Button
                            variant="outline"
                            size="small"
                            onClick={() => toggleCart(p.id)}
                            className="transition-all duration-150 hover:border-red-400 hover:text-red-600"
                          >
                            Retirer
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Divider className="my-4" />

              <div className="flex items-center justify-between mb-4">
                <Typography variant="h3" bold>
                  Total :
                </Typography>
                <Typography variant="h2" bold className="text-brand-600">
                  €{cartTotal.toFixed(2)}
                </Typography>
              </div>

              <div className="flex justify-center">
                <Button
                  variant="primary"
                  size="medium"
                  onClick={handlePay}
                  disabled={isPaying || cartItems.length === 0}
                  className="w-full font-bold uppercase tracking-wide shadow-md transition-all duration-150 hover:shadow-lg hover:bg-brand-700"
                  leftIcon={<span aria-hidden="true">💳</span>}
                >
                  {isPaying ? 'Traitement…' : `Payer`}
                </Button>
              </div>

              {message && (
                <Typography variant="body" className="mt-3 text-center">
                  {message}
                </Typography>
              )}
            </Card>
          </aside>
        </div>
      )}
    </div>
  );
}
